/**
 * personelRosterService.js — Firma personel listesi (`person_to_firma`) + SAKA onay kuyruğu,
 * kimlik sorgu, görevlendirme/eşleme istatistiği ve kişi dosyalarının kimlik doğrulamalı
 * servisi. Backend'in 2026-09-03'te eklediği DOKUZ ucun tamamı burada.
 *
 * Kaynak: docs/guides/FE-PersonnelController-guide.md §6 ve §7.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * TEK CÜMLE: `person_to_firma` = KİMİ atayabilirsin. `person_firma_to_fuar` = kimi
 * GERÇEKTEN atadın, tek bir fuarda.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * Bu ayrım bu dosyanın var oluş sebebi. `firmaPersonelListService.js` ve
 * `personnelAdminService.js` İKİNCİSİNİ (fuardaki kadro) okur; bu dosya BİRİNCİSİNİ
 * (firmanın daimî personel listesi) yönetir. İki listenin satır şekli farklı —
 * kadro satırında `documents`/`cardEligible`, liste satırında `rosterId`/`durum`/
 * `fuaraAtanabilir` var. Rehberin uyarısı: "Do not share a component between them
 * without checking the keys."
 *
 * ⚠️⚠️ NEDEN ŞİMDİ ÖNEMLİ: `POST /personnel/yeni` (kişiyi fuara ata) artık kişi firmanın
 * AKTİF listesinde değilse **409** döner — SAKA personeli için de. Tablo BOŞ başladı ve
 * geriye dönük doldurulmadı, yani her firma önce listesini kurmak zorunda. Dolayısıyla
 * "listeye ekle" artık her görevlendirme akışının ilk adımı, nadir bir istisna değil.
 * Yerelde canlı doğrulandı 2026-09-04: liste dışı kişi → 409, listedeki kişi → 200.
 *
 * ⚠️ Mock katmanı YOK — `personnelAdminService.js` / `kisiAdminService.js` ile aynı
 * gerekçe: bir onay kuyruğu yalnızca gerçek backend'e karşı anlamlı.
 *
 * ⚠️ TENANCY (rehber §Tenancy): 14 uç artık `user_to_firma`'ya göre kapsanıyor. Bir firma
 * hesabı yalnızca kendi listesini okuyup yazabilir (başkasınınki **403**), onay kuyruğunu
 * hiç göremez (**403**). SAKA personelinin kapsamı `null` — yani sınırsız.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n, { currentLocaleTag } from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/personnel`;

// ─── Sabitler ─────────────────────────────────────────────────────────────────────────

/**
 * ⛔ `person_to_firma.durum` (Bekliyor / Onaylandı / Reddedildi, Saka'nın bir işçiyi firmanın havuzuna
 * onayı) backend #112 (2026-09-29) ile EMEKLİ: yeni satır hep 1 yazılıyor, hiçbir yerde kapı olarak
 * okunmuyor ve `fuaraAtanabilir` yalnızca "satır aktif mi"dir. Bu yüzden onay sekmeleri
 * (`ROSTER_DURUM`, `getRosterDurumCounts`) kaldırıldı (backlog #111); satırda onun yerine
 * `kimlikDogrulamaDurum` gösterilir. Satırda alan hâlâ gelir ama KULLANMAYIN.
 */

/**
 * `aktif` süzgeci — backend #65 (2026-09-19). Satır SİLİNMEZ, `aktif = 0` yapılır; bu
 * süzgeç "bugünkü liste" ile "geçmişte kalan ilişkiler"i ayırır.
 *
 * ⚠️ String, sayı değil: `"hepsi"` bir seçenek olduğu için uç `String` alıyor ve geçersiz
 * bir değere 400 + geçerli değerleri sayan bir mesaj dönüyor. GÖNDERİLMEZSE varsayılan
 * `"1"`, yani #65 öncesi çağıranların hiçbiri değişmedi.
 */
export const ROSTER_AKTIF = { MEVCUT: '1', BITMIS: '0', HEPSI: 'hepsi' };

/** `bitis_kaynagi` — KararKaynagiConstant'ın bitiş etiketleri (1 Firma / 2 SAKA). */
export const ROSTER_BITIS_KAYNAK = {
  get 1() { return i18n.t("services:personelRosterService.firmaSonlandirdi"); },
  get 2() { return i18n.t("services:personelRosterService.sakaPersoneliSonlandirdi"); },
};

/** `bitis_sebebi` sınırı — backend 500'ü aşanı 400 ile reddediyor. */
export const BITIS_SEBEBI_MAX = 500;

/**
 * "İlişki sonlandırıldı mı, ne zaman, kim, neden" — İKİ AYRI SATIR ŞEKLİNİ normalize eder.
 *
 * Backend #65 aynı bilgiyi iki uçta FARKLI yazıyor ve bu bir tuzak:
 *   · Kadro satırı (`GET /personnel/firma/{id}?fuarId=`) → `iliski` ÖNEKLİ alanlar
 *     (`iliskiSonlandirildi`, `iliskiBitisTarihi`, …) ve tarih hazır biçimlenmiş string.
 *   · Liste satırı (`GET /personnel/firma/{id}/personel`) → öneksiz alanlar
 *     (`bitisTarihi`, `bitirenAdi`, …) ve tarih HAM sürücü değeri.
 * Aynı rozeti iki sayfada basabilmek için tek giriş noktası burası.
 *
 * ⚠️ Kadro satırında `iliskiSonlandirildi` yalnızca fuar FİİLEN sürüyorken true olabilir;
 * sürmüyorken sonlandırılmış kişi listeden tamamen düşer. Ayrıca `person_to_firma` satırı
 * HİÇ OLMAYAN ~68.000 eski görevlendirme için de false — "iz yok" bir bitiş değildir.
 */
export function iliskiBitisMeta(row) {
  if (!row) return { sonlandirildi: false };
  // Kadro satırı `iliskiSonlandirildi` taşır; liste satırında ölçüt `aktif === false`.
  const sonlandirildi = row.iliskiSonlandirildi === true || row.aktif === false;
  if (!sonlandirildi) return { sonlandirildi: false };

  const kaynak = row.iliskiBitisKaynagi ?? row.bitisKaynagi ?? null;
  return {
    sonlandirildi: true,
    tarih: formatTarihSaat(row.iliskiBitisTarihi ?? row.bitisTarihi),
    bitirenAdi: row.iliskiBitirenAdi ?? row.bitirenAdi ?? null,
    sebep: row.iliskiBitisSebebi ?? row.bitisSebebi ?? null,
    kaynak,
    kaynakAdi: row.iliskiBitisKaynagiAdi ?? row.bitisKaynagiAdi ?? ROSTER_BITIS_KAYNAK[kaynak] ?? 'Bilinmiyor',
  };
}

/** Kadro tarafı hazır string, liste tarafı ham değer gönderiyor — ikisini de karşılar. */
function formatTarihSaat(value) {
  if (!value) return null;
  const d = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(currentLocaleTag());
}

/**
 * `GET /kimlik-sorgu` → `yapilabilecek`. KARAR BACKEND'DE VERİLDİ — bu değere dallanın,
 * kuralı yeniden türetmeyin (rehber §7).
 */
export const KIMLIK_SORGU_SONUC = {
  GECERSIZ_KIMLIK_NO: {
    get label() { return i18n.t("services:personelRosterService.gecersizKimlikNo"); },
    color: 'danger',
    get aciklama() { return i18n.t("services:personelRosterService.kimlikNumarasiBosLutfen11"); },
    action: null,
  },
  YENI_KAYIT: {
    get label() { return i18n.t("services:personelRosterService.yeniKayit"); },
    color: 'primary',
    get aciklama() { return i18n.t("services:personelRosterService.buKimlikNumarasiylaKayitliKisi"); },
    action: 'CREATE',
  },
  EKLENEBILIR: {
    get label() { return i18n.t("services:personelRosterService.listeyeEklenebilir"); },
    color: 'success',
    get aciklama() { return i18n.t("services:personelRosterService.kisiSistemdeKayitliVePersonel"); },
    action: 'ADD_TO_ROSTER',
  },
  // `ONAY_GEREKLI` backend #112'de (2026-09-29) kaldırıldı — artık hiçbir yanıtta gelmiyor.
  YASAKLI: {
    get label() { return i18n.t("services:personelRosterService.girisiYasakli"); },
    color: 'danger',
    get aciklama() { return i18n.t("services:personelRosterService.buKisininFuarAlaninaGirisi"); },
    action: null,
  },
  MEVCUT_KAYIT: {
    get label() { return i18n.t("services:personelRosterService.mevcutKayit"); },
    color: 'info',
    get aciklama() { return i18n.t("services:personelRosterService.kisininTamKaydiAsagidaKarti"); },
    action: 'OPEN_CARD',
  },
};

/** `EslesmeTipiConstant` — aynı satırı kim yazdı (rehber §7). */
export const ESLESME_TIPI = {
  1: { get label() { return i18n.t("services:personelRosterService.gorevlendirme"); }, get aciklama() { return i18n.t("services:personelRosterService.firmaKendiKisisiniOncedenFuara"); } },
  2: { get label() { return i18n.t("services:personelRosterService.esleme"); }, get aciklama() { return i18n.t("services:personelRosterService.kisiyiBizFuarAlanindaEsledik"); } },
};

export const RED_SEBEBI_MAX = 500;

/**
 * `person_firma_to_fuar.durum` — FİRMANIN, Saka'nın yaptığı bir eşlemeye (`eslesme_tipi` 2) verdiği
 * cevap (backend #113). ⚠️ Emekli `person_to_firma.durum`la (yukarıdaki not) AYNI üç etiket,
 * FARKLI sütun — karıştırmayın.
 *
 *   · 0 Bekliyor  → hiçbir şeyi engellemez; firmalar portalı çoğu zaman kullanmaz ve Saka yine de
 *                   kişiyi alana alıp kartını basabilmeli.
 *   · 1 Onaylandı → firmanın kendi Görevlendirme'si de burada doğar (kendi eylemi).
 *   · 2 Reddedildi → kişi O FİRMA ÜZERİNDEN kart alamaz (`cardEligible` false, `uygunsuzluklar`da
 *                   FIRMA_REDDETTI, Özel İzin kaldırmaz). Satır listeden DÜŞMEZ; kişi aynı fuarda başka
 *                   bir firmasıyla yine kart alabilir.
 * `eslesmeDurumu` NULL = 2026-09-03 öncesi görevlendirme, kaydı yok (~68k satır). "Onaylandı" DEĞİL;
 * yalnızca süzgeçsiz (Tümü) görünümde çıkar.
 */
export const ESLESME_DURUM = {
  0: { get label() { return i18n.t("services:personelRosterService.bekliyor"); }, color: 'warning' },
  1: { get label() { return i18n.t("services:personelRosterService.onaylandi"); }, color: 'success' },
  2: { get label() { return i18n.t("services:personelRosterService.reddedildi"); }, color: 'danger' },
};

/** Sekme sırası; `eslesmeDurumu` süzgecinin geçerli değerleri de bunlar (başka değer 400). */
export const ESLESME_DURUM_IDS = [0, 1, 2];

// ─── Ortak yardımcılar ────────────────────────────────────────────────────────────────

// Backend 400/500'ü bazen düz metin, bazen {message,...} zarfı döner (bkz. rehber
// "Error bodies are inconsistent") — diğer servislerdeki aynı yardımcının kopyası.
// ⚠️ 401'in gövdesi TAMAMEN BOŞ gelir (Content-Length: 0) — asla JSON.parse etmeyin.
function parseError(error) {
  const message =
    typeof error.response?.data === 'string' && error.response.data
      ? error.response.data
      : error.response?.data?.message ?? error.message ?? i18n.t("services:personelRosterService.bilinmeyenHata");
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  throw wrapped;
}

/**
 * `{message, result, data}` zarfını açar ama MESAJI DA korur — bu modülde mesaj
 * verinin kendisi (hangi kapının açıldığını yalnızca o söylüyor, bkz. `addToRoster`).
 * ⚠️ `result:false` HTTP 200 ile gelir; kontrolü burada yapmak zorundayız.
 */
function unwrapEnvelope(response) {
  const body = response.data;
  if (body && typeof body === 'object' && 'result' in body) {
    if (body.result === false) {
      const err = new Error(body.message || i18n.t("services:personelRosterService.islemBasarisiz"));
      err.status = response.status;
      throw err;
    }
    return { message: body.message || '', data: body.data };
  }
  return { message: '', data: body };
}

/**
 * `POST /personnel/yeni`'nin 409'u — "kişi listede değil". Bir HATA DEĞİL, AKIŞ:
 * sayfa "önce listeye ekle" adımını önerip görevlendirmeyi tekrar denemeli.
 * (Dekoratörün taşeron adına görevlendirmesi ve onun ayrı 409 metni #381 ile kalktı.)
 */
export function isRosterMissingError(error) {
  return error?.status === 409;
}

/** Tenancy reddi — "başka firmanın verisi" (403). */
export function isTenancyRefusal(error) {
  return error?.status === 403;
}

// ─── §6 · Firma personel listesi (`person_to_firma`) ──────────────────────────────────

/**
 * Firmanın DAİMÎ personel listesi — fuardan bağımsız. Sıra: aktifler önce, sonra en son
 * sonlandırılanlar. Arama yalnızca ad+soyad'a bakar. Satırda `kimlikDogrulamaDurum` HER ZAMAN var
 * (0 Doğrulanmadı / 1 Doğrulandı / 2 Reddedildi; adı yok, etiket istemcide) ama kimliğe göre
 * süzen bir parametre YOK. Eski `durum` süzgeci emekli (bkz. yukarıdaki not).
 *
 * @param firmaId  yol parametresi — kendi firman ya da SAKA personeli; aksi halde 403
 *                 (taşeron delegasyonu #381 ile kalktı: dekoratör taşeronun listesini de okuyamaz)
 * @param aktif    ROSTER_AKTIF.MEVCUT | .BITMIS | .HEPSI; verilmezse backend "1" sayar
 * GET /api/v1/personnel/firma/{firmaId}/personel?aktif=&search=&page=&size=
 */
export async function getFirmaRoster(
  firmaId,
  { aktif, search = '', page = 1, size = 20 } = {},
) {
  try {
    const params = { page: page - 1, size };
    if (aktif !== undefined && aktif !== null) params.aktif = aktif;
    if (search && search.trim()) params.search = search.trim();
    const response = await axios.get(`${BASE}/firma/${firmaId}/personel`, {
      params,
      headers: getAuthHeader(),
    });
    const data = response.data; // { records, total, page, size, totalPages, erisim } — 0-tabanlı; `erisim` (TAM/DELEGE) artık okunmuyor
    return {
      total: data.total ?? 0,
      page: (data.page ?? 0) + 1,
      size: data.size ?? size,
      totalPages: data.totalPages ?? 1,
      records: data.records || [],
    };
  } catch (error) {
    parseError(error);
  }
}

/**
 * #743: bir seçicinin (PersonelSecici) sunabileceği kişiler — kaydın arkasındaki uç kimi kabul ediyorsa tam o kişiler.
 * Satır şekli getFirmaRoster'ınki, artı `listede` / `kayitTuru` (eski fuar çalışanında roster alanları null).
 *   fuarId yok → `kapsam=guncel`: firmanın GÜNCEL çalışanları, her fuar: aktif personel listesi satırı YA DA firmayla hiç
 *                liste geçmişi olmayan bir fuar çalışanı (eski kayıt). İlişiği kesilmiş olan yok. Paylaşımlı belge
 *                (SGK Hizmet Dökümü / İSG Katılım Listesi) ve belge taşıma bunu kabul eder.
 *   fuarId var → `kapsam=fuar`: firmanın o fuardaki, reddetmediği çalışanları (Elektrik taahhütnamesinin elektrikçisi).
 * Önceden seçici düz personel listesini (`getFirmaRoster`, yalnız person_to_firma) okuyordu; sunucuda hemen her
 * firmada boş olduğu için "Güncel personel listesinde kayıt yok." görünüyordu.
 * GET /api/v1/personnel/firma/{firmaId}/personel?kapsam=guncel|fuar&fuarId=&search=&page=&size=
 */
export async function getSecilebilirPersonel(
  firmaId,
  { fuarId = null, search = '', page = 1, size = 20 } = {},
) {
  try {
    const params = { kapsam: fuarId ? 'fuar' : 'guncel', page: page - 1, size };
    if (fuarId) params.fuarId = fuarId;
    if (search && search.trim()) params.search = search.trim();
    const response = await axios.get(`${BASE}/firma/${firmaId}/personel`, {
      params,
      headers: getAuthHeader(),
    });
    const data = response.data; // { records, total, page, size, totalPages, kapsam, fuarId } — 0-tabanlı
    return {
      total: data.total ?? 0,
      page: (data.page ?? 0) + 1,
      size: data.size ?? size,
      totalPages: data.totalPages ?? 1,
      records: data.records || [],
    };
  } catch (error) {
    parseError(error);
  }
}

/**
 * `calisanTipi` (backend #356) — kişinin bu firmadaki sınıfı, `person_to_firma` satırında SAKLANIR.
 * null = Belirsiz ("henüz ne olduğu belli değil"); kaynağı `calisanTipiKaynagi` (1 Belge / 2 Elle).
 * Etiketler portal sözlüğünden (`components:personelSekmeler.*`), burada yalnızca anahtar/id eşleşmesi.
 */
export const CALISAN_TIPI = { DAIMI: 1, GECICI: 2 };

/** `GET .../personel?sekme=` değerleri — backend `CalisanTipiConstant.SEKMELER` ile birebir. */
export const PERSONEL_SEKME = {
  TUM: 'tum', DAIMI: 'daimi', GECICI: 'gecici', BELIRSIZ: 'belirsiz',
  ILISIGI_KESILMIS: 'ilisigi-kesilmis', TAM: 'tam',
};

/** `altSekme` değerleri (yalnızca fuarId ile, tum/daimi/gecici/belirsiz sekmelerinde). */
export const PERSONEL_ALT_SEKME = {
  TUMU: 'tumu', KADRO: 'kadro', ONAY_BEKLEYEN: 'onay-bekleyen', GOREVLENDIRILMEMIS: 'gorevlendirilmemis',
};

/**
 * Firma Paneli personel listesi SEKMELERİ (backlog #356 / #357): kişi başına TEK satır, sekme rozet sayıları
 * (`sayilar`) ve — fuarId ile — alt sekme rozetleri (`sayilar.altSekmeler`, bir BÖLÜMLEME: kadro + onayBekleyen +
 * gorevlendirilmemis = tumu). Arama ve sayfalama her sekmede çalışır; rozetler aramadan etkilenmez.
 *
 * ⚠️ Fuar seçiliyken liste sekmeleri (tum/daimi/gecici/belirsiz) firmanın O FUARDA görevlendirdiği ama listesinde
 * OLMAYAN kişileri de getirir (`listede: false`, ~68.000 eski görevlendirmenin satırı yok) — süzmeyin, gösterin.
 * ⚠️ Yalnızca kendi firman ya da SAKA; taşeron (delege) çağrı kalktı (#381) ve `sekme` verilmişse 400 verirdi.
 * GET /api/v1/personnel/firma/{firmaId}/personel?sekme=&altSekme=&fuarId=&search=&page=&size= (0-tabanlı)
 */
export async function getFirmaPersonelSekme(
  firmaId,
  { sekme = PERSONEL_SEKME.TUM, altSekme, fuarId, search = '', page = 1, size = 20 } = {},
) {
  try {
    const params = { sekme, page: page - 1, size };
    if (fuarId) params.fuarId = fuarId;
    if (altSekme && fuarId) params.altSekme = altSekme;
    if (search && search.trim()) params.search = search.trim();
    const response = await axios.get(`${BASE}/firma/${firmaId}/personel`, {
      params,
      headers: getAuthHeader(),
    });
    const data = response.data;
    return {
      total: data.total ?? 0,
      page: (data.page ?? 0) + 1,
      size: data.size ?? size,
      totalPages: data.totalPages ?? 1,
      records: data.records || [],
      fuarCanli: data.fuarCanli ?? null,
      // { tum, daimi, gecici, belirsiz, ilisigiKesilmis, tam, altSekmeler?: { tumu, kadro, onayBekleyen, gorevlendirilmemis } }
      sayilar: data.sayilar ?? null,
      iliskiSonlandirabilir: data.iliskiSonlandirabilir !== false,
    };
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kişinin firmadaki çalışan tipini ELLE değiştirir (Daimî 1 / Geçici 2 / null Belirsiz) — aktif liste satırında,
 * satır kilitli, geçmiş tablosuna bir satır yazılır. `degisti: false` = zaten o tipteydi. Firma (`portal.personel.manage`)
 * kendi listesinde, SAKA herhangi bir firmada. Listede olmayan kişi (eski görevlendirme, sonlandırılmış) → 404.
 * PATCH /api/v1/personnel/firma/{firmaId}/personel/{personId}/calisan-tipi
 */
export async function setCalisanTipi(firmaId, personId, calisanTipi, { aciklama } = {}) {
  try {
    const body = { calisanTipi: calisanTipi ?? null };
    const temiz = String(aciklama ?? '').trim();
    if (temiz) body.aciklama = temiz;
    const response = await axios.patch(
      `${BASE}/firma/${firmaId}/personel/${personId}/calisan-tipi`,
      body,
      { headers: getAuthHeader() },
    );
    const { message, data } = unwrapEnvelope(response);
    return { message, ...(data || {}) };
  } catch (error) {
    parseError(error);
  }
}

// getFirmaKadroPersonIds (fuarın kadro personId kümesi, kadro ucunu 200'lük dilimlerle sonuna kadar okurdu) #357 ile kalktı:
// "kişi bu fuarda görevli mi" sorusunu artık sekmeli uç `fuarDurumu` ile satır başına cevaplıyor.

/**
 * Kişiyi firmanın personel listesine ekler.
 *
 * Backend #112'den (2026-09-29) beri EKLEME HEP ANINDA: satır `durum 1` ile yazılır, SAKA
 * onay kuyruğu yok (`GET/PATCH /personel-talepleri` silindi). Tek ret sebebi yasaklı kişi
 * (400 "Bu kişi yasaklı; personel listesine eklenemez.").
 *
 * ⚠️ İdempotent ve İKİ ANLAMLI dönüş — mesajla ayrışıyor:
 *   • YENİ satır     → 201, "Kişi personel listenize eklendi.", `data` = 1
 *   • VAR OLAN satır → 201 (aynı `result:true`), "Kişi zaten personel listenizde.",
 *     `data` = mevcut `person_to_firma_id`
 * Bu yüzden ham `data`'ya değil `already` bayrağına dallanın. Firma yanıtında "zaten listede"
 * için başka sinyal yok (`kimlik-sorgu`nun `dahaOnceCalisti`si fuar görevlendirmesidir, liste
 * üyeliği değil) — ekleme çağrısı bunu öğrenmenin tek yolu.
 *
 * ⚠️ Firma arayan için vergi no kapısı (`denyIfNoTaxNumber`) hâlâ bu uçta: firmanın vergi
 * numarası yoksa 400. SAKA bu kapıdan muaf.
 *
 * POST /api/v1/personnel/firma/{firmaId}/personel
 */
export async function addToRoster(firmaId, personId, { calisanTipi } = {}) {
  try {
    // #356: `calisan_tipi` (1 Daimî / 2 Geçici; yok ya da null = Belirsiz) yalnızca YENİ satıra yazılır, elle (kaynak Elle).
    // Zaten listedeki kişide değişiklik yapmaz — o iş setCalisanTipi'nin (PATCH).
    const body = { person_id: personId };
    if (calisanTipi === CALISAN_TIPI.DAIMI || calisanTipi === CALISAN_TIPI.GECICI) body.calisan_tipi = calisanTipi;
    const response = await axios.post(
      `${BASE}/firma/${firmaId}/personel`,
      body,
      { headers: getAuthHeader() },
    );
    const { message, data } = unwrapEnvelope(response);
    const already = /zaten personel listenizde/i.test(message);
    return {
      message,
      already,
      // Yeni satırda `data` durumdur (hep 1); var olan satırda satır id'sidir — karıştırmayın.
      durum: already ? null : data,
      rosterId: already ? data : null,
    };
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kişiyi personel listesinden çıkarır — satır SİLİNMEZ, `aktif = 0` yapılır.
 *
 * ⚠️ MEVCUT FUAR GÖREVLENDİRMELERİ BU ÇAĞRIYLA KALKMAZ. Canlı bir fuarda çalışan biri,
 * ofisteki bir ekran değişti diye alandan çıkmaz. Tam çıkış iki çağrıdır: bu + kadro
 * tarafındaki `DELETE /personnel/sil/{personId}` (personnelAdminService.removeFromFuarCrew).
 *
 * #65 (2026-09-19) ile satır artık KİM/NE ZAMAN/NEDEN de yazıyor:
 *   · `bitiren_user_id` ve `bitis_kaynagi` OTURUMDAN türetilir — gövdeye konulamaz,
 *     konulsa da görmezden gelinir. Firma çağırırsa 1, SAKA personeli çağırırsa 2.
 *   · `sebep` OPSİYONEL (bir redde zorunlu olan `red_sebebi`nin aksine), en fazla
 *     BITIS_SEBEBI_MAX karakter — aşarsa 400.
 *
 * ⚠️ Vergi numarası kapısı bu uçtan 2026-09-19'da KALDIRILDI (28.972 firmanın 0'ında vergi
 * no olduğu için hiç kimse ilişki sonlandıramıyordu). Diğer personel uçlarında duruyor.
 * ⚠️ 403 = başka firmanın satırı. Onun dışındaki her başarısızlık 404 döner — "listede
 * değil" ile başka bir hata ayırt edilemez, mesajı olduğu gibi gösterin.
 * DELETE /api/v1/personnel/firma/{firmaId}/personel/{personId}
 */
export async function removeFromRoster(firmaId, personId, { sebep } = {}) {
  try {
    const temiz = String(sebep ?? '').trim();
    const response = await axios.delete(`${BASE}/firma/${firmaId}/personel/${personId}`, {
      headers: getAuthHeader(),
      // Gövde opsiyonel; sebep yoksa hiç göndermiyoruz.
      ...(temiz ? { data: { sebep: temiz } } : {}),
    });
    return unwrapEnvelope(response).message;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Sonlandırılmış ilişkiyi GERİ ALIR (backend #109, 2026-09-29) — kişi aynı satırla yeniden
 * personel listesine döner (`aktif = 1`, bitiş alanları temizlenir); eski sonlandırma ayrı bir
 * geçmiş tablosuna yazılır (okuma ucu henüz yok). Fuar görevlendirmelerine dokunmaz: sonlandırma da
 * onlara hiç dokunmamıştı.
 *
 * Düğmeyi satırdaki `geriAlinabilir` belirler (çağırana göre sunucuda hesaplanır: firma yalnızca
 * KENDİ yaptığı sonlandırmayı görür, SAKA personeli hepsini) — kuralı istemcide yeniden kurmayın.
 * `sebep` OPSİYONEL, en fazla BITIS_SEBEBI_MAX. Mesajlar Türkçe ve olduğu gibi gösterilmeli:
 *   · 409 "Kişi zaten bu firmanın personel listesinde." (arada yeniden eklenmiş)
 *   · 404 "Kişinin bu firmada sonlandırılmış bir ilişkisi yok."
 *   · 400 sonlandıran kaydedilmeden önceki (#65 öncesi) bitiş / yasaklı kişi / uzun sebep
 *   · 403 başka firma, ya da firmanın SAKA'nın yaptığı sonlandırmayı geri almaya çalışması
 * ⚠️ Rehberde (`FE-PersonnelController-guide.md`) bu uç YOK; sözleşme Java kaynağından okundu.
 * POST /api/v1/personnel/firma/{firmaId}/personel/{personId}/geri-al
 */
export async function undoRosterEnding(firmaId, personId, { sebep } = {}) {
  try {
    const temiz = String(sebep ?? '').trim();
    const response = await axios.post(
      `${BASE}/firma/${firmaId}/personel/${personId}/geri-al`,
      temiz ? { sebep: temiz } : {},
      { headers: getAuthHeader() },
    );
    return unwrapEnvelope(response).message;
  } catch (error) {
    parseError(error);
  }
}

// SAKA onay kuyruğu (GET/PATCH /personel-talepleri) backend #112 ile silindi (#247): ekleme anında,
// onay yok. Eşleme istatistiği (aşağıda) ve firmanın eşleme cevabı (`answerEslesme`) ayrı uçlar, duruyor.

// ─── §7 · Kimlik sorgu ────────────────────────────────────────────────────────────────

/**
 * TAM EŞLEŞMELİ kimlik no sorgusu — önek araması DEĞİL, bir rehber DEĞİL.
 * Bir firmanın "geri dönen işçiyi tekrar ekleme"sinin tek yolu; bu uç yokken
 * `POST /yenikart` var olan kimlik no'yu reddediyor, `POST /yeni` ise `person_id`
 * istiyordu — 35 mükerrer TCKN grubunun doğrudan sebebi bu boşluktu.
 *
 * Yanıt ÇAĞIRANA GÖRE DARALTILIR (arayan türü sunucuda kullanıcı grubundan çıkar):
 *   • firma → backend #112'den (2026-09-29) beri yasaksız HER kişi için `EKLENEBILIR` +
 *     `personId` + `ad` + `soyad`; telefon, uyruk, KVKK, geçmiş görevler, kart sayısı YOK ve
 *     kişinin başka hangi firmada çalıştığı söylenmez. `dahaOnceCalisti` = kişi çağıranın
 *     firmalarında BİR FUARDA görevlendirilmiş (liste üyeliği DEĞİL). Yasaklıda `personId`
 *     ve ad bile gelmez — yalnızca `YASAKLI`. `ONAY_GEREKLI` artık üretilmiyor.
 *   • SAKA → tam kart + `gecmisGorevler` (⚠️ HAM snake_case anahtarlar) + `kartSayisi`;
 *     `yapilabilecek` hiçbir zaman EKLENEBILIR değil, `MEVCUT_KAYIT` (yasaklıysa `YASAKLI`,
 *     o zaman da personId/ad gelir). Ölçüt `personId` + `!yasakli`, katalogdaki `action` DEĞİL.
 *
 * ⚠️ "Kişi zaten bu firmanın listesinde mi?" yanıtta YOK; `addToRoster`ın idempotent
 * cevabından öğrenilir. Biçim/sağlama kontrolü yok: TCKN ile pasaport aynı alanda, tür
 * yalnızca `person_nationality_id`.
 *
 * `yapilabilecek` kararın kendisi — ona dallanın (`KIMLIK_SORGU_SONUC`).
 * Her sonuç 200 döner, "bulunamadı" dahil; 404 YOKTUR.
 * GET /api/v1/personnel/kimlik-sorgu?kimlikNo=
 */
export async function kimlikSorgu(kimlikNo) {
  try {
    const response = await axios.get(`${BASE}/kimlik-sorgu`, {
      params: { kimlikNo: String(kimlikNo ?? '').trim() },
      headers: getAuthHeader(),
    });
    const d = response.data || {};
    return {
      ...d,
      // Sonucun görsel karşılığı; bilinmeyen bir kod gelirse uydurmuyoruz.
      sonuc: KIMLIK_SORGU_SONUC[d.yapilabilecek] || null,
      // SAKA yanıtında bu alanlar var, firma yanıtında yok — sayfa buna bakıyor.
      staffView: Object.prototype.hasOwnProperty.call(d, 'gecmisGorevler'),
    };
  } catch (error) {
    parseError(error);
  }
}

/**
 * Firmanın, Saka'nın yaptığı bir eşlemeye cevabı (backend #113). SADECE firmanın kendi girişi:
 * Saka personeli 403. Yalnızca `eslesmeTipi` 2 (Eşleme) satırı
 * cevaplanır; Görevlendirme (firmanın kendi eylemi) ve kaydı olmayan eski satır 400 döner.
 *
 * ⚠️ `durum` JSON SAYISI olmalı — "1" (string) null sayılıp 400 verir. Reddederken `sebep` zorunlu
 * (en fazla RED_SEBEBI_MAX), onaylarken sunucu atar. Firma cevabını istediği zaman değiştirebilir;
 * aynı cevabı tekrarlamak 200 + "zaten kayıtlı", geçmişe satır yazmaz. Onay, önceki reddin
 * sebebini temizler. 409 = aynı anda başkası cevapladı, sayfayı yenileyin.
 * PATCH /api/v1/personnel/firma/{firmaId}/{personId}/eslesme-karari?fuarId=
 */
export async function answerEslesme(firmaId, personId, fuarId, { durum, sebep } = {}) {
  try {
    const body = { durum };
    if (durum === 2) body.sebep = String(sebep ?? '').trim();
    const response = await axios.patch(`${BASE}/firma/${firmaId}/${personId}/eslesme-karari`, body, {
      params: { fuarId },
      headers: getAuthHeader(),
    });
    return unwrapEnvelope(response).message;
  } catch (error) {
    parseError(error);
  }
}

// ─── §7 · Görevlendirme / Eşleme istatistiği ──────────────────────────────────────────

/**
 * İki istatistik: kişiyi FİRMA mı önceden görevlendirdi (1), yoksa BİZ mi alanda
 * eşledik (2). İkisi de aynı `person_firma_to_fuar` satırını yazar; fark aktördür ve
 * oturumun kullanıcı grubundan TÜRETİLİR, gövdeden asla kabul edilmez.
 *
 * ⚠️ `kisiSayisi` TEKİL kişi, `kayitSayisi` satır sayar — aynı fuarda iki firmanın
 * tuttuğu işçi alanda TEK kişidir. Manşet `kisiSayisi` olmalı.
 * ⚠️ `bilinmeyen` YALNIZCA fuar geneli (SAKA) görünümünde var; `firmaId` verilince
 * anahtar tamamen düşer — varlığını varsaymayın.
 * ⚠️ 2026-09-03 öncesi 67.889 görevlendirmenin tamamı `bilinmeyen` ve öyle kalacak
 * (geri doldurulamaz). Yani her mevcut fuarda iki sayaç da 0 okur — bu DOĞRU, bozuk
 * sorgu değil; sayfa bunu "kimse görevlendirilmemiş" diye sunmamalı.
 * GET /api/v1/personnel/eslesme-istatistik?fuarId=&firmaId=
 */
export async function getEslesmeIstatistik({ fuarId, firmaId } = {}) {
  try {
    const params = { fuarId };
    if (firmaId) params.firmaId = firmaId;
    const response = await axios.get(`${BASE}/eslesme-istatistik`, {
      params,
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

// ─── §7 · Kişi dosyaları — kimlik doğrulamalı akış ────────────────────────────────────

/**
 * ⛔ Kişi dosyaları `PublicFileController` whitelist'inde DEĞİL ve asla eklenmeyecek:
 * bir SGK işe giriş bildirgesi TC kimlik numarası taşır, `file_type` 90 ise kimlik
 * belgesinin fotoğrafıdır. KVKK'ya tabi kişisel veri, "tahmin edilemez dosya adı"
 * denetimiyle açık bir URL'e konmaz. Bu yüzden `/public/files/...` KULLANMAYIN —
 * dosyalar aşağıdaki iki kimlik doğrulamalı uçtan gelir.
 */

/** Ortak: kimlik doğrulamalı ikili indirir, hata zarfını okunur bir Error'a çevirir. */
async function fetchAuthedBlob(url) {
  try {
    const response = await axios.get(url, { headers: getAuthHeader(), responseType: 'blob' });
    return URL.createObjectURL(response.data);
  } catch (error) {
    // responseType:'blob' hata gövdesini de Blob yapar — JSON'u okumak için çözmek gerek.
    let message = error.message;
    let legacy = false;
    const blob = error.response?.data;
    if (blob instanceof Blob) {
      try {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        message = parsed.message || message;
        // "eski sistemde saklanıyor" = satır GERÇEK, baytlar PHP tarafında. Bunu
        // "belge bulunamadı" gibi göstermeyin, çok daha ürkütücü bir şey söyler.
        legacy = /eski sistemde/i.test(message);
      } catch {
        /* gövde JSON değilse mesajı olduğu gibi bırak */
      }
    }
    const wrapped = new Error(message);
    wrapped.status = error.response?.status ?? null;
    wrapped.legacy = legacy;
    throw wrapped;
  }
}

/**
 * Tek bir kişi belgesini indirir (object URL döner — kullanıp bitince
 * `URL.revokeObjectURL` çağırın).
 *
 * ⚠️ BUGÜN NEREDEYSE HER ÇAĞRI "LEGACY 404" DÖNER: 73.633 `file_to_person` bağının
 * 73.633'ü ve 31.638 vesikalığın 31.638'i PHP `repo/` ağacını gösteriyor. LEGACY
 * durumunu VARSAYILAN GÖRÜNÜM olarak kurun, hata bildirimi olarak değil —
 * `error.legacy` bunu ayırt eder.
 * GET /api/v1/personnel/{personId}/dosya/{fileId}
 */
export async function fetchKisiDosya(personId, fileId) {
  return fetchAuthedBlob(`${BASE}/${personId}/dosya/${fileId}`);
}

/**
 * Kişinin vesikalığı. Aynı erişim kuralı; aynı LEGACY uyarısı.
 * GET /api/v1/personnel/{personId}/fotograf
 */
export async function fetchKisiFotograf(personId) {
  return fetchAuthedBlob(`${BASE}/${personId}/fotograf`);
}
